import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, type FinancialYear } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

export default function AccountingPeriodsPage() {
  const { hospitalId, canPost } = useAccountingHospitalContext();
  const qc = useQueryClient();

  const { data: years = [], isLoading } = useQuery({
    queryKey: ["accounting-years", hospitalId],
    queryFn: () => accountingApi.listFinancialYears(hospitalId),
    enabled: !!hospitalId,
  });

  const ensureMut = useMutation({
    mutationFn: () => accountingApi.ensureFy(hospitalId),
    onSuccess: () => {
      toast.success("FY & periods ensured");
      qc.invalidateQueries({ queryKey: ["accounting-years", hospitalId] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Failed"),
  });

  const lockMut = useMutation({
    mutationFn: (periodId: string) => accountingApi.lockPeriod(periodId, hospitalId),
    onSuccess: () => {
      toast.success("Period locked");
      qc.invalidateQueries({ queryKey: ["accounting-years", hospitalId] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Lock failed"),
  });

  const reopenMut = useMutation({
    mutationFn: (periodId: string) => accountingApi.reopenPeriod(periodId, hospitalId),
    onSuccess: () => {
      toast.success("Period reopened");
      qc.invalidateQueries({ queryKey: ["accounting-years", hospitalId] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Reopen failed"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-medium">Financial years & periods</h2>
        {canPost ? (
          <Button
            size="sm"
            variant="outline"
            disabled={ensureMut.isPending}
            onClick={() => ensureMut.mutate()}
          >
            Ensure current FY
          </Button>
        ) : null}
      </div>

      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : null}

      {(years as FinancialYear[]).length === 0 ? (
        <p className="text-sm text-slate-500">
          No financial years yet. Ensure the current Indian FY (Apr–Mar).
        </p>
      ) : null}

      {(years as FinancialYear[]).map((fy) => (
        <div key={fy.id} className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{fy.label}</h3>
            <Badge variant="secondary">{fy.status}</Badge>
          </div>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {fy.periods.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.label}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {String(p.startDate).slice(0, 10)} → {String(p.endDate).slice(0, 10)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          p.status === "OPEN"
                            ? "default"
                            : p.status === "LOCKED"
                              ? "secondary"
                              : "outline"
                        }
                      >
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {canPost && p.status === "OPEN" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={lockMut.isPending}
                          onClick={() => lockMut.mutate(p.id)}
                        >
                          Lock
                        </Button>
                      ) : null}
                      {canPost && p.status === "LOCKED" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={reopenMut.isPending}
                          onClick={() => reopenMut.mutate(p.id)}
                        >
                          Reopen
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}
